import React from 'react'
import { Check } from 'lucide-react'

const PaymentTerms: React.FC = () => (
  <div className="max-w-4xl mx-auto px-8 py-10">
    <nav className="mb-6">
      <ol className="flex items-center gap-2 text-sm text-gray-500">
        <li>Docs</li>
        <li>/</li>
        <li className="text-gray-900 font-medium">Payment terms</li>
      </ol>
    </nav>

    <h1 className="text-3xl font-bold text-gray-900 mb-4">
      Payment Terms & <span className="text-primary-500">Commissions</span>
    </h1>
    <p className="text-gray-600 mb-12 leading-relaxed">
      Each course has a <strong className="font-medium text-gray-800">full course fee</strong> and a{' '}
      <strong className="font-medium text-gray-800">booking amount</strong> you set (upfront payment online).{' '}
      <strong className="font-medium text-gray-800">Bookmyyogattc charges 10% commission on the total course fee</strong>
      —not on the booking amount alone. That commission is deducted from the booking amount the student pays online.
      The booking amount must be <strong className="font-medium text-gray-800">more than 10%</strong> of the course fee
      so it can cover the platform&apos;s cut. The rest of the course fee is paid by the student{' '}
      <strong className="font-medium text-gray-800">directly to your school on arrival</strong>.
    </p>

    <div className="flex items-start gap-4 mb-8">
      <span className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white font-bold text-sm flex items-center justify-center rounded">
        01
      </span>
      <div className="flex-1">
        <h2 className="text-xl font-bold text-gray-900 mb-4">How booking payments work</h2>
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Step</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-200 bg-white">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">1. You set amounts</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  Full course fee plus booking amount per room. Booking amount must be greater than 10% of the course
                  fee (and cannot exceed the full fee).
                </td>
              </tr>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">2. Student pays online</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  Student pays the booking amount through Bookmyyogattc (not the full course fee).
                </td>
              </tr>
              <tr className="border-t border-gray-200 bg-white">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">3. Platform commission</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  BookMyYogaTTC deducts <strong>10% of the total course fee</strong> from the online payment.
                </td>
              </tr>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">4. School payout</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  You receive the booking amount minus the commission (10% of course fee).
                </td>
              </tr>
              <tr className="border-t border-gray-200 bg-white">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">5. Balance on arrival</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  Student pays course fee minus booking amount directly to your school on arrival.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div className="flex items-start gap-4">
      <span className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white font-bold text-sm flex items-center justify-center rounded">
        02
      </span>
      <div className="flex-1">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Example</h2>
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>Course fee (full price): <strong className="font-medium text-gray-900">$1,000</strong></span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>
                Platform commission (10% of course fee): <strong className="font-medium text-gray-900">$100</strong>
              </span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>
                Booking amount you set (e.g. $200 — more than 10% of $1,000): student pays this online
              </span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>
                Paid to your school from online payment ($200 − $100 commission):{' '}
                <strong className="font-medium text-gray-900">$100</strong>
              </span>
            </li>
            <li className="flex gap-2">
              <Check className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>
                Balance on arrival ($1,000 − $200):{' '}
                <strong className="font-medium text-gray-900">$800</strong> paid directly to your school
              </span>
            </li>
          </ul>
        </div>
        <p className="mt-4 text-sm text-gray-600 leading-relaxed">
          Commission is always <strong className="font-medium text-gray-800">10% of the total course fee</strong>.
          The booking amount must be above that 10% threshold so the online payment can cover the platform&apos;s
          commission before your payout.
        </p>
      </div>
    </div>
  </div>
)

export default PaymentTerms
